/**
 * Shared bisection helper for "first solid hit along a segment" queries that
 * have no native distance API — only an `isSolidAt(x,y,z) → boolean` probe.
 *
 * Used by `TiledBvh.segmentHitDistance` cross-tile fallback when a segment
 * spans two tiles (the per-tile BVH has a native raycast and uses that). A
 * future cleanup pass can replace this with a direct BVH raycast across tile
 * boundaries.
 *
 * Returns the parametric `t ∈ [0, 1]` of the first hit, or `null` if the
 * segment is clear. Step size = `voxelSize`; after a coarse hit, bisects 8
 * times for sub-voxel refinement.
 */
export function segmentHitBisect(
  x0: number, y0: number, z0: number,
  x1: number, y1: number, z1: number,
  voxelSize: number,
  isSolidAt: (x: number, y: number, z: number) => boolean,
): number | null {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const dz = z1 - z0;
  const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (dist < 1e-6) return isSolidAt(x1, y1, z1) ? 0 : null;
  const steps = Math.ceil(dist / voxelSize);
  let prevT = 0;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    if (isSolidAt(x0 + dx * t, y0 + dy * t, z0 + dz * t)) {
      let lo = prevT;
      let hi = t;
      for (let b = 0; b < 8; b++) {
        const mid = (lo + hi) * 0.5;
        if (isSolidAt(x0 + dx * mid, y0 + dy * mid, z0 + dz * mid)) {
          hi = mid;
        } else {
          lo = mid;
        }
      }
      return hi;
    }
    prevT = t;
  }
  return null;
}
